import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { usePopup } from '../context/PopupContext';
import './TestCreateWizard.css';

const TEST_TYPES = ['Aptitude Test', 'Diagnostic Test', 'Mock Placement', 'Practice Test', 'Company Specific Test'];

// Assessment category is an internal system property derived automatically from
// test_type on the backend — the admin never selects it. See deriveAssessmentCategory
// in server/controllers/testController.js for the authoritative mapping.

const TestCreateWizard = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const { showAlert, showConfirm } = usePopup();

  const [step, setStep] = useState(1);
  const [testId, setTestId] = useState(routeId || null);
  const [loading, setLoading] = useState(false);

  // STEP 1 STATE
  const [form, setForm] = useState({
    test_name: '', description: '', test_type: 'Aptitude Test', company_id: '',
    duration_minutes: 60, passing_percentage: 60, scheduled_at: '', availability: 'instant'
  });
  const [companies, setCompanies] = useState([]);

  // STEP 2 STATE
  const [allQuestions, setAllQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [qFilters, setQFilters] = useState({ topic: '', difficulty: '', company: '', type: '', search: '' });
  const [qVisible, setQVisible] = useState(50);

  // STEP 3 STATE
  const [audienceTab, setAudienceTab] = useState('students');
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [sVisible, setSVisible] = useState(50);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);

  // STEP 4 STATE
  const [settings, setSettings] = useState({
    randomize_questions: false, randomize_options: false, auto_submit: true, negative_marking: false,
    show_immediate_result: false, show_correct_answers: false, detailed_analysis: true,
    tab_switch_detection: true, browser_focus_tracking: true, copy_restriction: true, fullscreen_mode: true
  });

  useEffect(() => {
    fetchInitialData();
    if (testId) {
      fetchExistingTest(testId);
    }
  }, [testId]);

  const fetchExistingTest = async (id) => {
    try {
      const res = await API.get(`/tests/${id}`);
      const assignments = res.data.assignments || [];
      const hasSubmissions = assignments.some(a => a.status === 'submitted');
      if (hasSubmissions) {
        showAlert('Warning', 'Cannot edit a test that has submitted attempts.');
        navigate(`/admin/tests/${id}`);
        return;
      }
      const test = res.data.test;
      setForm({
        test_name: test.test_name,
        description: test.assessment_settings?.description || '',
        test_type: test.test_type,
        company_id: test.assessment_settings?.company_id || '',
        duration_minutes: test.duration_minutes,
        passing_percentage: test.assessment_settings?.passing_percentage || 60,
        scheduled_at: test.scheduled_at ? test.scheduled_at.slice(0, 16) : '',
        availability: test.scheduled_at ? 'scheduled' : 'instant'
      });
      if (test.assessment_settings) {
        setSettings({ ...settings, ...test.assessment_settings });
      }
      setSelectedQuestions(res.data.questions || []);
      const batchIds = res.data.assignments?.filter(a => a.target_type === 'batch').map(a => a.target_id) || [];
      const studentIds = res.data.assignments?.filter(a => a.target_type === 'student').map(a => a.target_id) || [];
      setSelectedBatches(batchIds);
      setSelectedStudents(studentIds);
    } catch (err) {
      console.error('Failed to load test details', err);
    }
  };

  const fetchInitialData = async () => {
    try {
      const [cRes, qRes, bRes, sRes] = await Promise.all([
        API.get('/companies').catch(() => ({ data: [] })),
        API.get('/questions?limit=1000').catch(() => ({ data: { data: [] } })),
        API.get('/batches').catch(() => ({ data: [] })),
        API.get('/students').catch(() => ({ data: [] }))
      ]);
      setCompanies(cRes.data || []);
      setAllQuestions(qRes.data?.data || qRes.data || []);
      setBatches(bRes.data || []);
      setStudents(sRes.data || []);
    } catch (err) {
      console.error('Failed to load initial data', err);
    }
  };

  const handleSaveBasic = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!testId) {
        const res = await API.post('/tests', form);
        setTestId(res.data.test.id);
      } else {
        await API.put(`/tests/${testId}`, form);
      }
      setStep(2);
    } catch (err) {
      showAlert('Error', err.response?.data?.message || 'Failed to save basic details');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (q) => {
    if (selectedQuestions.find(x => x.id === q.id)) {
      setSelectedQuestions(selectedQuestions.filter(x => x.id !== q.id));
    } else {
      setSelectedQuestions([...selectedQuestions, q]);
    }
  };

  const handleSaveQuestions = async () => {
    setLoading(true);
    try {
      const qIds = selectedQuestions.map(q => q.id);
      if (qIds.length > 0) {
        await API.post(`/tests/${testId}/questions`, { question_ids: qIds });
      }
      setStep(3);
    } catch (err) {
      showAlert('Error', err.response?.data?.message || 'Failed to map questions');
    } finally {
      setLoading(false);
    }
  };

  const toggleBatch = (bid) => selectedBatches.includes(bid) 
    ? setSelectedBatches(selectedBatches.filter(id => id !== bid)) 
    : setSelectedBatches([...selectedBatches, bid]);

  const toggleStudent = (sid) => selectedStudents.includes(sid)
    ? setSelectedStudents(selectedStudents.filter(id => id !== sid))
    : setSelectedStudents([...selectedStudents, sid]);

  const handleSaveAudience = async () => {
    setLoading(true);
    try {
      await API.post(`/tests/${testId}/assign`, {
        batch_ids: selectedBatches, student_ids: selectedStudents
      });
      setStep(4);
    } catch (err) {
      showAlert('Error', err.response?.data?.message || 'Failed to assign audience');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await API.put(`/tests/${testId}/settings`, { settings });
      setStep(5);
    } catch (err) {
      showAlert('Error', err.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (status) => {
    if (status === 'publish') {
      if (selectedQuestions.length === 0) {
        showAlert('Error', 'Cannot publish a test with 0 questions.');
        return;
      }
      if (selectedBatches.length === 0 && selectedStudents.length === 0) {
        const proceed = await showConfirm('No Audience', 'You have not assigned anyone to this test. Do you want to publish it now and assign later?');
        if (!proceed) return;
      } else {
        const proceed = await showConfirm('Publish Test', 'Are you sure you want to publish this test?');
        if (!proceed) return;
      }
    }

    setLoading(true);
    try {
      if (status === 'publish') {
        await API.post(`/tests/${testId}/publish`);
        showAlert('Success', 'Test published successfully!');
      } else {
        showAlert('Success', 'Test saved as draft!');
      }
      navigate('/admin/tests');
    } catch (err) {
      showAlert('Error', err.response?.data?.message || 'Failed to process test');
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestions = allQuestions.filter(q => {
    if (qFilters.search && !q.question_text?.toLowerCase().includes(qFilters.search.toLowerCase())) return false;
    if (qFilters.difficulty && q.difficulty !== qFilters.difficulty) return false;
    return true;
  });

  return (
    <div className="twiz-container">
      <div className="twiz-header">
        <div className="twiz-title">
          <h2>Create Assessment</h2>
          <p>Design a powerful evaluation experience.</p>
        </div>
        <div className="twiz-progress-bar" role="navigation" aria-label="Wizard Steps">
          {[1, 2, 3, 4, 5].map(s => (
            <div key={s} className={`twiz-step ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`} aria-current={step === s ? 'step' : undefined}>
              <div className="twiz-step-circle">{s}</div>
              <span className="twiz-step-label">
                {s===1?'Basic' : s===2?'Questions' : s===3?'Audience' : s===4?'Settings' : 'Review'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="twiz-content-glass">
        
        {/* STEP 1: BASIC DETAILS */}
        {step === 1 && (
          <form onSubmit={handleSaveBasic} className="twiz-split-layout">
            <div className="twiz-left-panel form-panel">
              <h3 className="panel-title">Basic Details</h3>
              <div className="form-group">
                <label>Test Name</label>
                <input required value={form.test_name} onChange={e => setForm({...form, test_name: e.target.value})} placeholder="e.g. TCS Diagnostic Test" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe the purpose of this test..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Test Type</label>
                  <select value={form.test_type} onChange={e => setForm({...form, test_type: e.target.value})}>
                    {TEST_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Company (Optional)</label>
                  <select value={form.company_id} onChange={e => setForm({...form, company_id: e.target.value})}>
                    <option value="">None</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Duration (mins)</label>
                  <input type="number" required min="5" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Passing %</label>
                  <input type="number" required min="1" max="100" value={form.passing_percentage} onChange={e => setForm({...form, passing_percentage: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Schedule Mode</label>
                <select value={form.availability} onChange={e => setForm({...form, availability: e.target.value, scheduled_at: ''})}>
                  <option value="instant">Instant / Anytime</option>
                  <option value="scheduled">Specific Scheduled Time</option>
                </select>
              </div>
              {form.availability === 'scheduled' && (
                <div className="form-group slide-in">
                  <label>Start Date & Time</label>
                  <input type="datetime-local" required value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})} />
                </div>
              )}
              <div className="twiz-footer">
                <button type="submit" disabled={loading} className="twiz-btn-primary">Next Step →</button>
              </div>
            </div>
            
            <div className="twiz-right-panel preview-panel">
              <h3 className="panel-title">Live Preview</h3>
              <div className="test-card-preview">
                <div className="tcp-header">
                  <span className="tcp-type">{form.test_type}</span>
                  <span className="tcp-duration">{form.duration_minutes} mins</span>
                </div>
                <h4 className="tcp-title">{form.test_name || 'Untitled Assessment'}</h4>
                <p className="tcp-desc">{form.description || 'No description provided.'}</p>
                <div className="tcp-meta">
                  <div className="tcp-stat"><strong>{form.passing_percentage}%</strong><span>Passing</span></div>
                  <div className="tcp-stat"><strong>{selectedQuestions.length}</strong><span>Questions</span></div>
                </div>
                <button className="tcp-btn" disabled>Start Test</button>
              </div>
            </div>
          </form>
        )}

        {/* STEP 2: QUESTIONS */}
        {step === 2 && (
          <div className="twiz-tri-layout">
            <div className="twiz-col twiz-filters">
              <h3 className="panel-title">Filters</h3>
              <input placeholder="Search questions..." className="twiz-search" value={qFilters.search} onChange={e => setQFilters({...qFilters, search: e.target.value})} />
              <select value={qFilters.difficulty} onChange={e => setQFilters({...qFilters, difficulty: e.target.value})}>
                <option value="">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            
            <div className="twiz-col twiz-qbank">
              <h3 className="panel-title">Question Bank</h3>
              <div className="twiz-qlist">
                {filteredQuestions.slice(0, qVisible).map(q => (
                  <div key={q.id} className="twiz-qcard">
                    <div className="qcard-main">
                      <span className={`qcard-diff ${q.difficulty}`}>{q.difficulty}</span>
                      <p className="qcard-text">{q.question_text.slice(0, 80)}...</p>
                    </div>
                    <button className="qcard-add" onClick={() => toggleQuestion(q)}>
                      {selectedQuestions.find(x => x.id === q.id) ? '➖' : '➕ Add'}
                    </button>
                  </div>
                ))}
                {filteredQuestions.length > qVisible && (
                  <button className="twiz-btn-text" onClick={() => setQVisible(prev => prev + 50)}>Load More</button>
                )}
              </div>
            </div>

            <div className="twiz-col twiz-selected-qs">
              <h3 className="panel-title">Selected ({selectedQuestions.length})</h3>
              <div className="twiz-metrics">
                <div><span>Total Marks:</span> <strong>{selectedQuestions.length}</strong></div>
                <div><span>Est. Time:</span> <strong>{selectedQuestions.length * 1.5}m</strong></div>
              </div>
              <div className="twiz-qlist">
                {selectedQuestions.map((q, idx) => (
                  <div key={q.id} className="twiz-qcard selected">
                    <span className="qcard-num">{idx + 1}</span>
                    <p className="qcard-text">{q.question_text.slice(0, 40)}...</p>
                    <button className="qcard-rem" onClick={() => toggleQuestion(q)}>✕</button>
                  </div>
                ))}
              </div>
              <div className="twiz-footer vertical">
                <button onClick={handleSaveQuestions} disabled={loading} className="twiz-btn-primary full">Save & Continue</button>
                <button onClick={() => setStep(1)} className="twiz-btn-text">Back to Basic Details</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: AUDIENCE */}
        {step === 3 && (
          <div className="twiz-split-layout">
            <div className="twiz-left-panel audience-panel">
              <div className="twiz-tabs">
                <button className={audienceTab === 'students' ? 'active' : ''} onClick={() => setAudienceTab('students')}>Students</button>
                <button className={audienceTab === 'batches' ? 'active' : ''} onClick={() => setAudienceTab('batches')}>Batches</button>
              </div>
              
              <div className="audience-list">
                {audienceTab === 'batches' ? (
                  <div className="grid-list">
                    {batches.map(b => (
                      <div key={b.id} className={`audience-card ${selectedBatches.includes(b.id) ? 'selected' : ''}`} onClick={() => toggleBatch(b.id)}>
                        <div className="ac-check">✓</div>
                        <div className="ac-content">
                          <h4>{b.name || b.batch_name}</h4>
                          <span>{b.student_count || 0} Students</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="table-list">
                     <table className="twiz-table">
                       <thead>
                         <tr><th>Select</th><th>Name</th><th>Roll Number</th></tr>
                       </thead>
                       <tbody>
                         {students.slice(0, sVisible).map(s => (
                           <tr key={s.id} onClick={() => toggleStudent(s.id)} className={selectedStudents.includes(s.id)?'selected':''}>
                             <td><input type="checkbox" checked={selectedStudents.includes(s.id)} readOnly aria-label={`Select student ${s.name}`} /></td>
                             <td>{s.name}</td>
                             <td>{s.roll_number}</td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                     {students.length > sVisible && (
                       <div style={{textAlign: 'center', padding: '10px'}}>
                         <button className="twiz-btn-text" onClick={() => setSVisible(prev => prev + 50)}>Load More</button>
                       </div>
                     )}
                  </div>
                )}
              </div>
            </div>

            <div className="twiz-right-panel summary-panel">
              <h3 className="panel-title">Assignment Summary</h3>
              <div className="summary-stat">
                <span>Selected Batches</span>
                <strong>{selectedBatches.length}</strong>
              </div>
              <div className="summary-stat">
                <span>Selected Students</span>
                <strong>{selectedStudents.length}</strong>
              </div>
              <div className="twiz-footer vertical">
                <button onClick={handleSaveAudience} disabled={loading} className="twiz-btn-primary full">Save & Continue</button>
                <button onClick={() => setStep(2)} className="twiz-btn-text">Back to Questions</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: SETTINGS */}
        {step === 4 && (
          <div className="twiz-settings-layout">
            <h3 className="panel-title">Assessment Settings</h3>
            <div className="settings-groups">
              
              <div className="settings-group">
                <h4><span className="sg-icon">⚙️</span> Test Experience</h4>
                <label className="toggle-label">
                  <div className="tl-text">
                    <strong>Randomize Questions</strong>
                    <span>Deliver questions in random order for each student.</span>
                  </div>
                  <input type="checkbox" className="twiz-toggle" checked={settings.randomize_questions} onChange={e => setSettings({...settings, randomize_questions: e.target.checked})} />
                </label>
                <label className="toggle-label">
                  <div className="tl-text">
                    <strong>Randomize Options</strong>
                    <span>Shuffle multiple choice options.</span>
                  </div>
                  <input type="checkbox" className="twiz-toggle" checked={settings.randomize_options} onChange={e => setSettings({...settings, randomize_options: e.target.checked})} />
                </label>
                <label className="toggle-label">
                  <div className="tl-text">
                    <strong>Negative Marking</strong>
                    <span>Deduct marks for incorrect answers.</span>
                  </div>
                  <input type="checkbox" className="twiz-toggle" checked={settings.negative_marking} onChange={e => setSettings({...settings, negative_marking: e.target.checked})} />
                </label>
              </div>

              <div className="settings-group">
                <h4><span className="sg-icon">🔒</span> Security & Proctoring</h4>
                <label className="toggle-label">
                  <div className="tl-text">
                    <strong>Tab Switch Detection</strong>
                    <span>Warn or submit if student switches browser tabs.</span>
                  </div>
                  <input type="checkbox" className="twiz-toggle" checked={settings.tab_switch_detection} onChange={e => setSettings({...settings, tab_switch_detection: e.target.checked})} />
                </label>
                <label className="toggle-label">
                  <div className="tl-text">
                    <strong>Fullscreen Enforced</strong>
                    <span>Force test to be taken in full screen mode.</span>
                  </div>
                  <input type="checkbox" className="twiz-toggle" checked={settings.fullscreen_mode} onChange={e => setSettings({...settings, fullscreen_mode: e.target.checked})} />
                </label>
              </div>

              <div className="settings-group">
                <h4><span className="sg-icon">📊</span> Results Visibility</h4>
                <label className="toggle-label">
                  <div className="tl-text">
                    <strong>Show Immediate Results</strong>
                    <span>Students see their score immediately after submission.</span>
                  </div>
                  <input type="checkbox" className="twiz-toggle" checked={settings.show_immediate_result} onChange={e => setSettings({...settings, show_immediate_result: e.target.checked})} />
                </label>
              </div>

            </div>
            <div className="twiz-footer">
              <button onClick={() => setStep(3)} className="twiz-btn-secondary">← Back</button>
              <button onClick={handleSaveSettings} disabled={loading} className="twiz-btn-primary">Save & Continue →</button>
            </div>
          </div>
        )}

        {/* STEP 5 & 6: REVIEW AND PUBLISH */}
        {step >= 5 && (
          <div className="twiz-review-layout">
            <h3 className="panel-title text-center">Ready to Publish?</h3>
            <div className="review-columns">
              <div className="rev-col">
                <div className="rc-icon">📄</div>
                <h4>Basic Details</h4>
                <ul>
                  <li><strong>Name:</strong> {form.test_name}</li>
                  <li><strong>Type:</strong> {form.test_type}</li>
                  <li><strong>Duration:</strong> {form.duration_minutes} mins</li>
                  <li><strong>Pass %:</strong> {form.passing_percentage}%</li>
                </ul>
              </div>
              <div className="rev-col">
                <div className="rc-icon">❓</div>
                <h4>Questions</h4>
                <div className="rc-big-stat">{selectedQuestions.length}</div>
                <p>Total questions selected</p>
              </div>
              <div className="rev-col">
                <div className="rc-icon">👥</div>
                <h4>Audience</h4>
                <ul>
                  <li><strong>Batches:</strong> {selectedBatches.length}</li>
                  <li><strong>Students:</strong> {selectedStudents.length}</li>
                </ul>
              </div>
            </div>
            
            <div className="twiz-publish-actions">
              <button onClick={() => handlePublish('draft')} className="twiz-btn-outline">Save as Draft</button>
              <button onClick={() => handlePublish('publish')} className="twiz-btn-publish">🚀 Publish Now</button>
            </div>
            <div className="text-center mt-3">
               <button onClick={() => setStep(4)} className="twiz-btn-text">Wait, go back</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestCreateWizard;
